import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

const SURVEY_API = process.env.REACT_APP_SURVEY_API_URL || "https://eamico-survey-api.eamico-inc.workers.dev";

export default function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error | already
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const encoded = searchParams.get("e");
    if (encoded) {
      try {
        setEmail(atob(encoded));
      } catch { /* ignore bad encoding */ }
    }
  }, [searchParams]);

  const handleUnsubscribe = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) {
      setErrorMsg("Please enter a valid email address.");
      setStatus("error");
      return;
    }

    setStatus("loading");
    setErrorMsg("");

    try {
      const res = await fetch(`${SURVEY_API}/api/waitlist/unsubscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });
      const data = await res.json();

      if (data.ok) {
        setStatus("success");
      } else if (data.already) {
        setStatus("already");
      } else {
        setErrorMsg(data.error || "Something went wrong.");
        setStatus("error");
      }
    } catch {
      setErrorMsg("Could not reach our servers. Please try again.");
      setStatus("error");
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: "#ffffff", fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}
      data-testid="unsubscribe-page"
    >
      <div className="w-full max-w-md text-center">
        {/* Logo */}
        <img
          src="https://customer-assets.emergentagent.com/job_eamico-core/artifacts/uowh0b6l_Untitled%20design.png"
          alt="Eamico"
          className="mx-auto mb-16"
          style={{ height: 32 }}
        />

        {status === "success" ? (
          <div data-testid="unsubscribe-success">
            <h1 style={{ fontSize: 36, fontWeight: 200, color: "#1a1a1a", letterSpacing: -0.5, marginBottom: 16, lineHeight: 1.2 }}>
              You've been unsubscribed.
            </h1>
            <p style={{ fontSize: 16, fontWeight: 300, color: "#6e6e6e", lineHeight: 1.7, marginBottom: 32 }}>
              We've removed you from all Eamico emails. If this was a mistake, you can always rejoin at{" "}
              <a href="https://eamico.com" style={{ color: "#e8850e", textDecoration: "none" }}>eamico.com</a>.
            </p>
            <p style={{ fontSize: 14, fontWeight: 300, color: "#aaaaaa" }}>We're sorry to see you go.</p>
          </div>
        ) : status === "already" ? (
          <div data-testid="unsubscribe-already">
            <h1 style={{ fontSize: 36, fontWeight: 200, color: "#1a1a1a", letterSpacing: -0.5, marginBottom: 16, lineHeight: 1.2 }}>
              Already unsubscribed.
            </h1>
            <p style={{ fontSize: 16, fontWeight: 300, color: "#6e6e6e", lineHeight: 1.7 }}>
              This email is already off our list. You won't receive anything from us.
            </p>
          </div>
        ) : (
          <div data-testid="unsubscribe-form">
            <h1 style={{ fontSize: 36, fontWeight: 200, color: "#1a1a1a", letterSpacing: -0.5, marginBottom: 8, lineHeight: 1.2 }}>
              Unsubscribe
            </h1>
            <p style={{ fontSize: 16, fontWeight: 300, color: "#6e6e6e", lineHeight: 1.7, marginBottom: 36 }}>
              We get it. Enter your email below and we'll remove you from all Eamico communications.
            </p>

            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="unsubscribe-email-input"
              onKeyDown={(e) => e.key === "Enter" && handleUnsubscribe()}
              style={{
                width: "100%",
                padding: "14px 20px",
                fontSize: 16,
                fontWeight: 300,
                fontFamily: "inherit",
                border: "1px solid #e0e0e0",
                borderRadius: 8,
                outline: "none",
                color: "#1a1a1a",
                boxSizing: "border-box",
                marginBottom: 16,
                transition: "border-color 0.2s",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#e8850e")}
              onBlur={(e) => (e.target.style.borderColor = "#e0e0e0")}
            />

            <button
              onClick={handleUnsubscribe}
              disabled={status === "loading"}
              data-testid="unsubscribe-submit-btn"
              style={{
                width: "100%",
                padding: "14px 20px",
                fontSize: 16,
                fontWeight: 400,
                fontFamily: "inherit",
                color: "#ffffff",
                background: "linear-gradient(135deg, #e8850e, #f5a623)",
                border: "none",
                borderRadius: 8,
                cursor: status === "loading" ? "wait" : "pointer",
                opacity: status === "loading" ? 0.6 : 1,
                transition: "opacity 0.2s",
                letterSpacing: 0.3,
              }}
            >
              {status === "loading" ? "Removing..." : "Unsubscribe"}
            </button>

            {status === "error" && (
              <p data-testid="unsubscribe-error" style={{ marginTop: 16, fontSize: 14, color: "#d93025" }}>{errorMsg}</p>
            )}
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: 64, paddingTop: 20, borderTop: "1px solid #f0f0f0" }}>
          <p style={{ fontSize: 10, color: "#cccccc", letterSpacing: 0.3 }}>Eamico, Inc. — Toronto, Ontario, Canada</p>
        </div>
      </div>
    </div>
  );
}
